// components/monitoring/DailyBreakdownChart.js
// Per-day usage chart for weekly & monthly period reports.
// Consumes the `dailyBreakdown` array returned by the weekly/monthly endpoints:
//   [{ date, downMB, upMB, clientCount, uptime, lastStatus,
//      perWan: [{ wan_id, wan_name, downMB, upMB }, ...] }, ...]
//
// Two views:
//   'total'  -> Download vs Upload columns (period total per day)
//   'byWan'  -> stacked columns, one series per WAN (total throughput per WAN per day)
import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const UNIT_OPTIONS = [
  { key: 'MB', divisor: 1 },
  { key: 'GB', divisor: 1024 },
  { key: 'TB', divisor: 1024 ** 2 },
];

// Palette reused for WAN series (cycles if more WANs than colors).
const WAN_COLORS = [
  '#1B4965', '#2C7DA0', '#00A896', '#4F772D',
  '#6BBF59', '#E09F3E', '#9E2A2B', '#540B0E',
];

const DailyBreakdownChart = ({ dailyBreakdown = [], period = 'weekly' }) => {
  const [unit, setUnit] = useState('GB');
  const [view, setView] = useState('total'); // 'total' | 'byWan'

  // Sort ascending by date so the x-axis reads left -> right chronologically.
  const rows = useMemo(() => {
    return [...(dailyBreakdown || [])].sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  }, [dailyBreakdown]);

  const divisor = UNIT_OPTIONS.find((u) => u.key === unit)?.divisor || 1024;

  const categories = rows.map((r) => {
    const parts = String(r.date).split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : r.date;
  });

  // Does the payload actually carry per-WAN data? (Backend `perWan` field.)
  const hasPerWan = useMemo(
    () => rows.some((r) => Array.isArray(r.perWan) && r.perWan.length > 0),
    [rows]
  );

  // ── TOTAL view series ────────────────────────────────────────────────
  const downloadData = rows.map((r) => Number(((r.downMB || 0) / divisor).toFixed(2)));
  const uploadData = rows.map((r) => Number(((r.upMB || 0) / divisor).toFixed(2)));
  const totalDown = downloadData.reduce((a, b) => a + b, 0);
  const totalUp = uploadData.reduce((a, b) => a + b, 0);

  // ── BY-WAN view series ───────────────────────────────────────────────
  // Build a stable WAN list across all days, keyed by wan_id (fallback name).
  const wanSeries = useMemo(() => {
    if (!hasPerWan) return [];

    const wanMeta = new Map(); // key -> { key, name }
    rows.forEach((r) => {
      (r.perWan || []).forEach((w) => {
        const key = w.wan_id != null ? `id:${w.wan_id}` : `name:${w.wan_name}`;
        if (!wanMeta.has(key)) {
          wanMeta.set(key, { key, name: w.wan_name || `WAN ${w.wan_id}` });
        }
      });
    });

    const wanKeys = [...wanMeta.keys()];
    return wanKeys.map((key, i) => {
      const meta = wanMeta.get(key);
      const data = rows.map((r) => {
        const match = (r.perWan || []).find(
          (w) => (w.wan_id != null ? `id:${w.wan_id}` : `name:${w.wan_name}`) === key
        );
        // Total throughput per WAN per day = down + up (MB) -> unit.
        const mb = match ? (match.downMB || 0) + (match.upMB || 0) : 0;
        return Number((mb / divisor).toFixed(2));
      });
      return {
        name: meta.name,
        data,
        color: WAN_COLORS[i % WAN_COLORS.length],
      };
    });
  }, [rows, hasPerWan, divisor]);

  const isByWan = view === 'byWan' && hasPerWan;

  const options = {
    chart: {
      type: 'column',
      height: 350,
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
    },
    title: { text: null },
    xAxis: {
      categories,
      labels: {
        rotation: categories.length > 14 ? -45 : 0,
        style: { fontSize: '10px', color: 'var(--color-label)' },
      },
      lineColor: 'var(--color-border)',
      tickColor: 'var(--color-border)',
    },
    yAxis: {
      title: {
        text: isByWan ? `Throughput by WAN (${unit})` : `Data Usage (${unit})`,
        style: { color: 'var(--color-label)' },
      },
      labels: { style: { color: 'var(--color-label)' } },
      gridLineColor: 'var(--color-border)',
      stackLabels: {
        enabled: isByWan,
        style: { color: 'var(--color-label)', fontSize: '9px', fontWeight: '600' },
        formatter: function () {
          return this.total ? this.total.toFixed(1) : '';
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      headerFormat: '<b>{point.key}</b><br/>',
      pointFormat:
        '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b> ' +
        unit +
        '<br/>',
    },
    plotOptions: {
      column: {
        borderRadius: 0,
        stacking: isByWan ? 'normal' : undefined,
        groupPadding: 0.1,
        pointPadding: 0.05,
      },
    },
    colors: ['var(--color-download)', 'var(--color-upload)'],
    series: isByWan
      ? wanSeries
      : [
          { name: 'Download', data: downloadData, color: 'var(--color-download)' },
          { name: 'Upload', data: uploadData, color: 'var(--color-upload)' },
        ],
    credits: { enabled: false },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { fontWeight: 'normal', color: 'var(--color-label)' },
    },
  };

  if (!rows.length) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">
          No daily breakdown available for this {period === 'monthly' ? 'month' : 'week'}.
        </p>
      </div>
    );
  }

  const btnStyle = (active) => ({
    fontSize: '10px',
    fontFamily: "'DM Mono', monospace",
    letterSpacing: '0.04em',
    padding: '4px 9px',
    borderRadius: 0,
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    background: active ? 'var(--color-secondary)' : 'var(--color-surface)',
    color: active ? 'var(--navy-800, #112023)' : 'var(--color-label)',
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: '0.8rem', color: 'var(--color-label)' }}>
          <strong style={{ color: 'var(--color-download)' }}>
            ↓ {totalDown.toFixed(2)} {unit}
          </strong>
          <strong style={{ color: 'var(--color-upload)', marginLeft: 8 }}>
            ↑ {totalUp.toFixed(2)} {unit}
          </strong>
          <span style={{ marginLeft: 8 }}>over {rows.length} days</span>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* View toggle (only when per-WAN data exists) */}
          {hasPerWan && (
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={() => setView('total')} style={btnStyle(view === 'total')}>
                Total
              </button>
              <button onClick={() => setView('byWan')} style={btnStyle(view === 'byWan')}>
                By WAN
              </button>
            </div>
          )}

          {/* Unit toggle */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {UNIT_OPTIONS.map((u) => (
              <button key={u.key} onClick={() => setUnit(u.key)} style={btnStyle(unit === u.key)}>
                {u.key}
              </button>
            ))}
          </div>
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default DailyBreakdownChart;
